import { Box, Link, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as Logo } from "../../../assets/svg/logo.svg";
import { getLoginSchema } from "../../../utils/schemas/AuthSchemas";
import { joiResolver } from "@hookform/resolvers/joi";
import { LoginForm } from "../types";
import { useMutation } from "react-query";
import { AuthAPI } from "../../../utils/api/requests/auth-requests";
import { authStore } from "../store/AuthStore";
import { AxiosError } from "../../../utils/api/axiosErrorType";
import { useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { tagManagerEvent } from "../../../utils/tagManagerEvent";
import { useTranslation } from "react-i18next";

export const Login = () => {
  const { t } = useTranslation();
  const { setSessionId } = useContext(authStore);
  const { search } = useLocation();
  const navigate = useNavigate();

  const form = useForm<LoginForm>({
    mode: "onChange",
    resolver: joiResolver(getLoginSchema()),
  });
  const { handleSubmit, control } = form;

  const { mutate, isLoading } = useMutation({
    mutationFn: AuthAPI.getSessionId,

    onSuccess: ({ access_token }) => {
      setSessionId(access_token);
    },
    onError: (error) => {
      const err = error as AxiosError;
      if (err.response?.data?.detail === "User is not confirmed") {
        toast.error(err?.response?.data?.detail);
        navigate("/confirm-email");
      } else if (err.response?.status === 422) {
        toast.error("The data provided is invalid. Please check your input and try again.");
      } else {
        toast.error(err?.response?.data?.detail || "An error occurred. Please try again later.");
      }
    },
  });

  const onSubmit = async (formData: LoginForm) => {
    tagManagerEvent("Login", "Login_button");
    mutate(formData);
  };

  const { mutate: confirmEmail } = useMutation({
    mutationFn: AuthAPI.confirmEmail,
    onSuccess: () => {
      toast.success("Your Email Address Has Been Confirmed");
      navigate("/login");
    },
    onError: (error) => {
      const err = error as AxiosError;
      toast.error(err?.response?.data?.detail);
    },
  });

  useEffect(() => {
    const token = search?.split("=")[1];
    if (token) {
      confirmEmail(token);
    }
  }, [confirmEmail, navigate, search]);

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      onSubmit={handleSubmit(onSubmit)}
      marginTop="40px"
      sx={{ width: "100%" }}
    >
      <Box sx={{ display: "flex", pb: "24px" }}>
        <Logo height={"50px"} width={"156px"} />
      </Box>
      <Box sx={{ pb: "32px" }}>
        <Typography fontSize={"1.3rem"} fontStyle={"bold"}>
          {t("auth.LogIn")}
        </Typography>
        <Typography
          fontSize={"0.875rem"}
          color={"GrayText"}
          display="inline"
          mr="4px"
        >
          {t("auth.noAccount")}
        </Typography>
        <Link
          component={RouterLink}
          fontSize={"0.875rem"}
          to={{ pathname: "/signup" }}
          sx={{ cursor: "pointer" }}
          underline="hover"
        >
          {t("auth.register")}
        </Link>
      </Box>

      <Controller
        control={control}
        name="username"
        defaultValue=""
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <>
            <TextField
              {...field}
              inputRef={ref}
              id="standard-error-helper-text"
              //   helperText={"Email Address"}
              label={t("auth.username")}
              variant="outlined"
              autoComplete="off"
              sx={{
                marginBottom: "24px",
              }}
              error={!!error}
              helperText={error?.message}
            />
          </>
        )}
      />
      <Controller
        control={control}
        name="password"
        defaultValue=""
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <>
            <TextField
              {...field}
              inputRef={ref}
              type="password"
              label={t("auth.password")}
              variant="outlined"
              autoComplete="off"
              sx={{
                marginBottom: "24px",
              }}
              error={!!error}
            />
          </>
        )}
      />
      <LoadingButton
        type="submit"
        variant="contained"
        size="large"
        color={"primary"}
        loading={isLoading}
      >
        {t("auth.continue")}
      </LoadingButton>
      <Link
        component={RouterLink}
        to={{ pathname: "/forgot" }}
        sx={{ cursor: "pointer", mt: "24px" }}
        fontSize={"0.875rem"}
        underline="hover"
      >
        {t("auth.forgotPassword")}
      </Link>
    </Box>
  );
};
